/**
 * Bulk itinerary status change — criteria, preview, and apply contracts.
 *
 * Distinct from `itineraries.reassignStatus`, which moves EVERY itinerary of
 * one status to another (the mechanism for emptying a status before deleting
 * it). This surface is criteria-scoped and selection-based: the operator
 * narrows a set, reviews it, unticks rows, and applies to exactly what is
 * left. The apply step therefore takes explicit ids, never a criteria object —
 * re-resolving criteria at write time would silently include rows created
 * between preview and apply.
 */

import { z } from 'zod';

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return (
      !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
    );
  }, 'Invalid ISO date');

/**
 * Travel-start timestamps that encode "no real travel date". Both are written
 * by the app, not typed by users, so they are data sentinels rather than dates:
 *
 * - **2000** — `UNDATED_SENTINEL_START_ISO`, stamped on undated itineraries for
 *   legacy read parity (`isUndatedEntity` = year 2000).
 * - **1970** — epoch zero, left behind by imports that had no start date.
 *
 * They matter here because an "exclude these travel years" rule passes them
 * *vacuously*: a 1970 row is not in 2026/2027, so a literal reading sweeps it
 * in. `includeNoTravelDate` makes that an explicit operator decision.
 */
export const NO_TRAVEL_DATE_YEARS = [1970, 2000] as const;

export const bulkStatusCriteria = z.object({
  // Exclusive upper bound on `itinerary.created_on`, compared as a UTC date.
  createdBefore: dateOnly.optional(),
  // Travel-start years to EXCLUDE from the match (the "start date is not year
  // X" half of the rule). Empty means no travel-year restriction.
  excludeStartYears: z
    .array(z.number().int().min(1900).max(2200))
    .max(20)
    .default([]),
  // Source statuses. Non-empty by design: without it the criteria would target
  // every itinerary in the organization, which no bulk status change wants.
  statusIds: z.array(z.uuid()).min(1).max(50),
  // Opt-in for the sentinel rows described on NO_TRAVEL_DATE_YEARS. Defaults
  // to false so a travel-year rule never sweeps in dateless records silently.
  includeNoTravelDate: z.boolean().default(false),
});

export type BulkStatusCriteria = z.infer<typeof bulkStatusCriteria>;

/**
 * Upper bound on the id list returned by `preview`. "Select all matching" needs
 * every id client-side to build an exact apply payload, so the cap is what
 * stops an unnarrowed criteria set from returning a multi-megabyte response.
 * Beyond it the UI reports `idCapExceeded` and asks the operator to narrow.
 */
export const BULK_STATUS_ID_CAP = 10_000;

export const bulkStatusPreviewInput = z.object({
  criteria: bulkStatusCriteria,
  // Windows the human-readable `rows` only. `totalCount` and `matchingIds`
  // always describe the whole match.
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(25),
});

export type BulkStatusPreviewInput = z.infer<typeof bulkStatusPreviewInput>;

export const bulkStatusPreviewRow = z.object({
  id: z.uuid(),
  reference: z.string().nullable(),
  title: z.string().nullable(),
  statusId: z.uuid().nullable(),
  statusName: z.string().nullable(),
  statusColor: z.string().nullable(),
  customerName: z.string().nullable(),
  createdOn: z.string(),
  startTime: z.string().nullable(),
  // Precomputed server-side so the table can badge sentinel rows without
  // re-deriving the year rule in the client.
  hasNoTravelDate: z.boolean(),
});

export type BulkStatusPreviewRow = z.infer<typeof bulkStatusPreviewRow>;

export const bulkStatusPreviewOutput = z.object({
  totalCount: z.number().int().nonnegative(),
  matchingIds: z.array(z.uuid()),
  idCapExceeded: z.boolean(),
  rows: z.array(bulkStatusPreviewRow),
  breakdown: z.object({
    byStatus: z.array(
      z.object({
        statusId: z.uuid(),
        statusName: z.string().nullable(),
        count: z.number().int().nonnegative(),
      })
    ),
    noTravelDateCount: z.number().int().nonnegative(),
  }),
});

export type BulkStatusPreviewOutput = z.infer<typeof bulkStatusPreviewOutput>;

/**
 * `operation_log.initiator` values that identify this tool's envelope rows.
 *
 * The envelope `entry_type` (`itinerary_status_bulk_reassign`) is a Postgres
 * enum label shared with `itineraries.reassignStatus`, so the initiator is what
 * separates the two tools' runs — and what separates a run from its undo.
 */
export const BULK_STATUS_INITIATOR = 'itineraries.bulkStatusChange';
export const BULK_STATUS_UNDO_INITIATOR = 'itineraries.bulkStatusChange.undo';

export const bulkStatusApplyInput = z.object({
  itineraryIds: z.array(z.uuid()).min(1).max(BULK_STATUS_ID_CAP),
  targetStatusId: z.uuid(),
  // Snapshot of the criteria that produced the selection. Recorded on the run
  // log so the history can say what the operator searched for, not just what
  // moved. Optional: the run is defined by its explicit id list, never by this.
  criteria: bulkStatusCriteria.optional(),
});

export type BulkStatusApplyInput = z.infer<typeof bulkStatusApplyInput>;

/**
 * Why an id in the apply payload was not moved. Every id the caller sent comes
 * back either counted in `changed` or listed here — the operator's post-run
 * report has to account for the whole selection, not just the successes.
 */
export const bulkStatusSkipReason = z.enum([
  // Already sitting on the target status; moving it would write a no-op audit
  // pair and a misleading `cancelled_on`.
  'already_in_target',
  // Not visible to this organization, or soft-deleted between preview and
  // apply.
  'not_found',
]);

export type BulkStatusSkipReason = z.infer<typeof bulkStatusSkipReason>;

export const bulkStatusApplyOutput = z.object({
  changedCount: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      id: z.uuid(),
      reference: z.string().nullable(),
      reason: bulkStatusSkipReason,
    })
  ),
  targetStatus: z.object({
    id: z.uuid(),
    status: z.string().nullable(),
    salesStage: z.string().nullable(),
  }),
  // What the shared sale-date contract did to the batch, so the report can
  // state it rather than leaving the operator to infer it from the stage.
  saleDateEffect: z.enum(['stamped_cancelled', 'stamped_sold', 'none']),
});

export type BulkStatusApplyOutput = z.infer<typeof bulkStatusApplyOutput>;

/**
 * One past run, as shown in the History tab.
 *
 * Runs are not a table of their own — they are the `operation_log` envelope
 * rows this tool already writes, read back. `id` is therefore the envelope
 * row's id, which is also what `undo` takes.
 */
export const bulkStatusRun = z.object({
  id: z.uuid(),
  createdOn: z.string(),
  createdById: z.uuid().nullable(),
  // Resolved "First Last" (or email) of the operator who ran it; null for rows
  // whose account was deleted.
  createdByName: z.string().nullable(),
  targetStatusId: z.uuid().nullable(),
  targetStatusName: z.string().nullable(),
  // Source statuses the run actually moved itineraries off, resolved from the
  // recorded snapshot rather than from the criteria — the criteria says what
  // was searched for, this says what moved.
  sourceStatuses: z.array(
    z.object({
      statusId: z.uuid().nullable(),
      statusName: z.string().nullable(),
      count: z.number().int().nonnegative(),
    })
  ),
  changedCount: z.number().int().nonnegative(),
  criteria: bulkStatusCriteria.nullable(),
  // Undo state. `undoneOn` is set once the run has been reversed; a run can be
  // undone at most once.
  undoneOn: z.string().nullable(),
  undoneByName: z.string().nullable(),
  // False for runs recorded before the tool captured a per-itinerary snapshot —
  // those stay visible for audit but cannot be reversed as a unit.
  isUndoable: z.boolean(),
});

export type BulkStatusRun = z.infer<typeof bulkStatusRun>;

export const bulkStatusRunsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(50).default(10),
});

export type BulkStatusRunsInput = z.infer<typeof bulkStatusRunsInput>;

export const bulkStatusRunsOutput = z.object({
  runs: z.array(bulkStatusRun),
  total: z.number().int().nonnegative(),
});

export type BulkStatusRunsOutput = z.infer<typeof bulkStatusRunsOutput>;

export const bulkStatusUndoInput = z.object({
  // The envelope `operation_log` row id — `bulkStatusRun.id`.
  runId: z.uuid(),
});

export type BulkStatusUndoInput = z.infer<typeof bulkStatusUndoInput>;

/**
 * Why an itinerary in the run was not restored.
 *
 * `changed_since_run` is the load-bearing one: undo only reverses itineraries
 * still sitting on the status the run set. Anything moved since — by a
 * consultant, another bulk run, or an individual undo — is left alone rather
 * than being silently rolled back over the top of that newer decision.
 */
export const bulkStatusUndoSkipReason = z.enum([
  'changed_since_run',
  'not_found',
]);

export type BulkStatusUndoSkipReason = z.infer<typeof bulkStatusUndoSkipReason>;

export const bulkStatusUndoOutput = z.object({
  restoredCount: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      id: z.uuid(),
      reference: z.string().nullable(),
      reason: bulkStatusUndoSkipReason,
    })
  ),
});

export type BulkStatusUndoOutput = z.infer<typeof bulkStatusUndoOutput>;
